import React, { useEffect, useState, useRef } from 'react'
import { createFormBag, FormBag, FormData } from 'react-formage';

import { ButtonGroup } from 'components/ui/button'
import { FooterEditEntity } from 'components/ui/footer'
import { Input } from 'components/ui/input'
import { MediaField } from 'domains/media/components/media-field'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { StatusGuard } from 'components/ui/status-guard'

import { sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'
import { audioCarouselItemValueSchema, videoCarouselItemValueSchema, imageCarouselItemValueSchema } from 'domains/org/getty/schemas/carousel-item'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyStore } from 'domains/org/getty/store'
import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'

import { UpdateCarouselItemRequest } from 'generated/mos/org/getty/audiomanagement'
import { CarouselItemValue, CarouselItem } from 'generated/mos/org/getty/audio'
import { GetCarouselItemRequest } from 'generated/mos/org/getty/audioaccess'
import { Media } from 'generated/mos/media'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'

import { pick, assertNever, areRefValuesEqual } from 'helpers/core'
import { Status, statusSelector } from 'helpers/status'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

type FormValues = {
  creditText?: string | undefined;
  imageAltText?: string | undefined;
  publicImageTitle?: string | undefined;
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyCreateCarouselItemReset' | 'gettyGetCarouselItemRequest' | 'gettyUpdateCarouselItemRequest' | 'gettyUpdateCarouselItemReset'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'carouselItemCreate' | 'carouselItemUpdate'>

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly carouselItemId: string;
  readonly stopId: string;
  readonly tourId?: string;
};
type Props = ActionProps & ConnectedProps & DirectProps;

export const CarouselItemUpdateView = (props: Props) => {
  const {
    carouselItemCreate,
    carouselItemId,
    carouselItemUpdate,
    gettyCreateCarouselItemReset,
    gettyGetCarouselItemRequest,
    gettyUpdateCarouselItemRequest,
    gettyUpdateCarouselItemReset,
    historyPush,
    projectId,
    stopId,
    toastNotification,
    tourId,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
  } = props

  const [ audioValue, setAudioValue ] = useState<Media.Ref | undefined>(undefined)
  const [ imageValue, setImageValue ] = useState<Media.Ref | undefined>(undefined)
  const [ videoValue, setVideoValue ] = useState<Media.Ref | undefined>(undefined)

  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(createFormBag({}))

  // where to return to if the user wants to navigate back.
  // if we came here from within a tour, go back to that tour, otherwise go to all stops.
  const backLocation = tourId ? `/project/${projectId}/tour/${tourId}/stop/${stopId}/media-carousel` : `/project/${projectId}/stop/${stopId}/media-carousel`

  const carouselItemData = statusSelector.data(carouselItemUpdate)
  const carouselItemValue = carouselItemData && carouselItemData.value
  const carouselItemValueItem = carouselItemData && carouselItemData.value && carouselItemData.value.item

  const hasSubmitted = useRef(false)

  useEffect(() => {
    if (carouselItemUpdate.status === Status.Idle || (carouselItemData && CarouselItem.mustRef(carouselItemData.ref).id !== carouselItemId)) {
      gettyGetCarouselItemRequest({
        type: GetCarouselItemRequest.refName,
        carouselItemRef: {
          typename: CarouselItem.refName,
          id: carouselItemId,
        }
      })
      releaseControlsListReleaseControlsRequest([{
        typename: CarouselItem.refName,
        id: carouselItemId,
      }])
    }
    if (carouselItemCreate.status === Status.Complete) {
      toastNotification({
        type: 'success',
        text: 'Carousel item created successfully.'
      })
      gettyCreateCarouselItemReset()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // if the status is ready and the data had been submitted, trigger success toast
    if (carouselItemUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([{
        typename: CarouselItem.refName,
        id: carouselItemId,
      }])
      toastNotification({
        type: 'success',
        text: 'Carousel item updated'
      })
      hasSubmitted.current = false
    }

    // ensure that local state for all media values is clear before setting new values
    setAudioValue(undefined)
    setImageValue(undefined)
    setVideoValue(undefined)

    // keep the current media value up to date in state
    if (carouselItemUpdate.status === Status.Ready && carouselItemValueItem) {
      switch (carouselItemValueItem.type) {
        case 'mos.org.getty.audio.CarouselItemValue.AudioItem': {
          setAudioValue(audioCarouselItemValueSchema.audio.read(carouselItemValueItem.audio) as any as Media.Ref)
          break
        }
        case 'mos.org.getty.audio.CarouselItemValue.VideoItem': {
          setVideoValue(videoCarouselItemValueSchema.video.read(carouselItemValueItem.video) as any as Media.Ref)
          break
        }
        case 'mos.org.getty.audio.CarouselItemValue.HeroImageItem': {
          break
        }
        case 'mos.org.getty.audio.CarouselItemValue.ImageItem': {
          setImageValue(imageCarouselItemValueSchema.fullScreenImageRef.read(carouselItemValueItem.fullScreenImageRef) as any as Media.Ref)
          setFormBag(
            createFormBag({
              creditText: imageCarouselItemValueSchema.creditText.read(carouselItemValueItem.creditText) as string,
              imageAltText: imageCarouselItemValueSchema.altText.read(carouselItemValueItem.altText) as string,
              publicImageTitle: imageCarouselItemValueSchema.publicImageTitle.read(carouselItemValueItem.publicImageTitle) as string,
            }
          ))
          break
        }

        default: {
          assertNever(carouselItemValueItem)
        }
      }
    }
  }, [carouselItemUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps


  // construct an object used to set the page content depending on the carousel item type
  const getPageDetails = () => {
    if (carouselItemValueItem === undefined || (carouselItemData && CarouselItem.mustRef(carouselItemData.ref).id !== carouselItemId)) {
      return
    }

    switch (carouselItemValueItem.type) {
      case 'mos.org.getty.audio.CarouselItemValue.ImageItem': {
        return ({
          formFields: <>
            <MediaField
              kind="MEDIA_KIND_RASTER_IMAGE"
              mediaRef={imageValue}
              onUpdate={(entity) => setImageValue(entity ? entity.ref : undefined) }
              label="Image"
            />
            <Input
              label="Alternate Text"
              description="Description of what the image contains if it cannot be shown."
              field="imageAltText"
              isAssociated={true}
            />
            <Input
              label="Image Copyright"
              description="Appended to the image caption"
              field="creditText"
              isAssociated={true}
            />
            <Input
              label="Public Image Title"
              description="Title of the image."
              field="publicImageTitle"
              isAssociated={true}
            />
          </>,
          title: 'Image',
          imageMedia: imageCarouselItemValueSchema.fullScreenImageRef.read(carouselItemValueItem.fullScreenImageRef) as any as Media.Ref,
          initalValues: {
            creditText: imageCarouselItemValueSchema.creditText.read(carouselItemValueItem.creditText) as string,
            imageAltText: imageCarouselItemValueSchema.altText.read(carouselItemValueItem.altText) as string,
            publicImageTitle: imageCarouselItemValueSchema.publicImageTitle.read(carouselItemValueItem.publicImageTitle) as string
          }
        })
      }
      case 'mos.org.getty.audio.CarouselItemValue.AudioItem': {
        return ({
          formFields: <MediaField
            kind="MEDIA_KIND_AUDIO"
            mediaRef={audioValue}
            onUpdate={(entity) => { setAudioValue(entity ? entity.ref : undefined) }}
            label="Audio"
          />,
          title: 'Audio',
          audioMedia: audioCarouselItemValueSchema.audio.read(carouselItemValueItem.audio) as any as Media.Ref
        })
      }
      case 'mos.org.getty.audio.CarouselItemValue.VideoItem': {
        return ({
          formFields: <MediaField
            kind="MEDIA_KIND_VIDEO"
            mediaRef={videoValue}
            onUpdate={(entity) => { setVideoValue(entity ? entity.ref : undefined) }}
            label="Video"
          />,
          title: 'Video',
          videoMedia: videoCarouselItemValueSchema.video.read(carouselItemValueItem.video) as any as Media.Ref
        })
      }
      case 'mos.org.getty.audio.CarouselItemValue.HeroImageItem': {
        return
      }
      default: {
        assertNever(carouselItemValueItem)
      }
    }
  }

  const pageDetails = carouselItemValue && getPageDetails()

  const imageInputFieldsModified = formBag && pageDetails && pageDetails.initalValues && (
    formBag.values.creditText !== pageDetails.initalValues.creditText ||
    formBag.values.imageAltText !== pageDetails.initalValues.imageAltText ||
    formBag.values.publicImageTitle !== pageDetails.initalValues.publicImageTitle
  )

  const modified = pageDetails &&
    (!areRefValuesEqual(pageDetails.audioMedia, audioValue) ||
    !areRefValuesEqual(pageDetails.imageMedia, imageValue) ||
    !areRefValuesEqual(pageDetails.videoMedia, videoValue)) ||
    imageInputFieldsModified

  const updatedCarouselItemValue = () => {
    if (carouselItemValueItem === undefined) {
      return undefined
    }
    switch (carouselItemValueItem.type) {
      case 'mos.org.getty.audio.CarouselItemValue.AudioItem': {
        return({
          ...CarouselItemValue.AudioItem.defaults,
          audio: audioCarouselItemValueSchema.audio.write(audioValue)
        })
      }
      case 'mos.org.getty.audio.CarouselItemValue.HeroImageItem': {
        break
      }
      case 'mos.org.getty.audio.CarouselItemValue.ImageItem': {
        return({
          ...CarouselItemValue.ImageItem.defaults,
          carouselImageRef: imageCarouselItemValueSchema.carouselImageRef.write(imageValue),
          fullScreenImageRef: imageCarouselItemValueSchema.fullScreenImageRef.write(imageValue),
          altText: imageCarouselItemValueSchema.altText.write(formBag ? formBag.values.imageAltText : undefined),
          creditText: imageCarouselItemValueSchema.creditText.write(formBag ? formBag.values.creditText : undefined),
          publicImageTitle: imageCarouselItemValueSchema.publicImageTitle.write(formBag ? formBag.values.publicImageTitle : undefined),
        })
      }
      case 'mos.org.getty.audio.CarouselItemValue.VideoItem': {
        return({
          ...CarouselItemValue.VideoItem.defaults,
          video: videoCarouselItemValueSchema.video.write(videoValue)
        })
      }

      default: {
        assertNever(carouselItemValueItem)
      }
    }

  }

  const onSave = () => {
    if (carouselItemValue) {
      carouselItemValue.item && carouselItemValue.item.type

      gettyUpdateCarouselItemRequest({
        ...UpdateCarouselItemRequest.defaults,
        ref: {
          typename: CarouselItem.refName,
          id: carouselItemId
        },
        updateMask: {
          type: "google.protobuf.FieldMask",
          fields: ['value'],
        },
        value: {
          ...CarouselItemValue.defaults,
          item: updatedCarouselItemValue()
        }
      })
    }
    hasSubmitted.current = true
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    stop && releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: {
        typename: CarouselItem.refName,
        id: carouselItemId,
      },
      bundles,
    })
  }

  return (
    <>
      <StatusGuard status={carouselItemUpdate}>
        {carouselItemValue && formBag &&
          <LayoutListOrFormPage
            nav={() => (
              <PrimaryNavigationEditEntity
                title="Edit Slide"
                onConfirm={modified ? () => historyPush(backLocation) : undefined}
                navBackRoute={backLocation}
              />
            )}
            title={pageDetails ? pageDetails.title : ''}
            toolbar={() => (
              <ButtonGroup>
                {carouselItemId &&
                  <ReleaseControlUpdate
                    hasBorder
                    entityRef={{
                      typename: CarouselItem.refName,
                      id: carouselItemId,
                    }}
                    updateHandler={releaseControlUpdate}
                  />
                }
              </ButtonGroup>
            )}
            content={() => (
              <StatusGuard status={carouselItemUpdate} mode="form">
                {pageDetails && (
                  <form noValidate onSubmit={(event) => { event.preventDefault() }} >
                    <FormData
                      bag={formBag}
                      onUpdate={(event) => {setFormBag(event.bag)}}
                      validate={() => ({})}
                    >
                      {pageDetails.formFields}
                    </FormData>
                  </form>
                )}
              </StatusGuard>
            )}
            sidebar={() => null}
            footer={() => (
              <FooterEditEntity
                entity={carouselItemUpdate}
                modified={modified}
                onCancel={() => {
                  gettyUpdateCarouselItemReset()
                  historyPush(backLocation)
                }}
                onSave={onSave}
              />
          )}
          />
        }
      </StatusGuard>
    </>
  )
}

export const CarouselItemUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, 'carouselItemCreate', 'carouselItemUpdate'),
  }),
  {
    ...pick(actionCreators, 'gettyCreateCarouselItemReset', 'gettyGetCarouselItemRequest', 'gettyUpdateCarouselItemRequest', 'gettyUpdateCarouselItemReset'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(CarouselItemUpdateView)
